import {DocumentArrayGroup, DocumentModel} from '@/engine';

/**
 * Extracts template field patterns from an array group definition.
 * For example, extracts "J17", "J18" from fields like "J17#{$index}", "J18#{$index}".
 *
 * @param group - The array group definition
 * @returns Array of base field patterns (e.g., ["J17", "J18"])
 */
export const extractTemplatePatterns = (group: DocumentArrayGroup): string[] => {
  const templatePatterns: string[] = [];
  group.rows.forEach(row => {
    row.fields.forEach(field => {
      // Extract base field ID pattern (e.g., "J17" from "J17#{$index}")
      const fieldIdPattern = field.id.replace(/\{\$index\}/g, '');
      if (fieldIdPattern.includes('#')) {
        const basePattern = fieldIdPattern.split('#')[0];
        if (!templatePatterns.includes(basePattern)) {
          templatePatterns.push(basePattern);
        }
      }
    });
  });
  return templatePatterns;
};

/**
 * Finds an array group by ID in the document model.
 *
 * @param documentModel - The document model to search
 * @param arrayId - The array group ID to find
 * @returns The array group if found, undefined otherwise
 */
export const findArrayGroupInModel = (
  documentModel: DocumentModel,
  arrayId: string,
): DocumentArrayGroup | undefined => {
  for (const section of documentModel.sections) {
    for (const group of section.groups) {
      if ('rows' in group && group.id === arrayId) {
        return group;
      }
    }
  }
  return undefined;
};

/**
 * Finds the maximum array index for a given array group by scanning all data keys.
 * This scans BOTH metadata fields (_arrayId#N[...]) AND data fields (from template patterns).
 * This ensures we detect all array elements, even if they exceed the declared count or
 * lack proper metadata.
 *
 * @param data - The document data object
 * @param arrayId - The array group ID
 * @param groupOrModel - Either the array group definition directly, or the document model to search in
 * @returns The highest index found, or -1 if no elements exist
 */
export const findMaxArrayIndex = (
  data: Record<string, unknown>,
  arrayId: string,
  groupOrModel?: DocumentArrayGroup | DocumentModel,
): number => {
  let maxIndex = -1;

  // 1. Scan metadata fields (_arrayId#N[...])
  const metadataPattern = new RegExp(`^_${arrayId}#(\\d+)\\[`);
  for (const key of Object.keys(data)) {
    const match = key.match(metadataPattern);
    if (match) {
      const index = parseInt(match[1], 10);
      if (index > maxIndex) {
        maxIndex = index;
      }
    }
  }

  // 2. If group or model is provided, also scan for template field patterns
  if (groupOrModel) {
    let arrayGroup: DocumentArrayGroup | undefined;

    // Determine if we received a group directly or need to find it in the model
    if ('sections' in groupOrModel) {
      // It's a DocumentModel, find the array group
      arrayGroup = findArrayGroupInModel(groupOrModel, arrayId);
    } else if ('rows' in groupOrModel) {
      // It's already a DocumentArrayGroup
      arrayGroup = groupOrModel;
    }

    // Extract template field patterns and scan data keys
    if (arrayGroup) {
      const templatePatterns = extractTemplatePatterns(arrayGroup);

      // Scan all data keys for fields matching the template patterns
      for (const key of Object.keys(data)) {
        for (const pattern of templatePatterns) {
          const regex = new RegExp(`^${pattern}#(\\d+)$`);
          const match = key.match(regex);
          if (match) {
            const index = parseInt(match[1], 10);
            if (index > maxIndex) {
              maxIndex = index;
            }
          }
        }
      }
    }
  }

  return maxIndex;
};

/**
 * Collects all field IDs that belong to a specific array group by scanning data keys.
 * This includes:
 * - Fields from the template pattern (e.g., J17#{$index})
 * - Fields listed in the [fields] metadata
 *
 * @param data - The document data object
 * @param group - The array group definition
 * @param maxIndex - The maximum index to scan up to
 * @returns Array of all field IDs found for this array group
 */
export const collectAllArrayFields = (
  data: Record<string, unknown>,
  group: DocumentArrayGroup,
  maxIndex: number,
): string[] => {
  const allFieldIds = new Set<string>();

  // 1. Extract template field patterns from the group definition
  const templatePatterns = extractTemplatePatterns(group);

  // 2. Scan all data keys for fields matching the template patterns
  for (const key of Object.keys(data)) {
    for (const pattern of templatePatterns) {
      const regex = new RegExp(`^${pattern}#(\\d+)$`);
      const match = key.match(regex);
      if (match) {
        const index = parseInt(match[1], 10);
        if (index <= maxIndex) {
          allFieldIds.add(key);
        }
      }
    }
  }

  // 3. Also collect from [fields] metadata if available
  for (let index = 0; index <= maxIndex; index++) {
    const fieldsListString = data[`_${group.id}#${index}[fields]`];
    if (typeof fieldsListString === 'string' && fieldsListString) {
      const rowFieldIds = fieldsListString.split(',');
      rowFieldIds.forEach(fieldId => allFieldIds.add(fieldId));
    }
  }

  return Array.from(allFieldIds);
};
